import { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Palette, Check } from '@phosphor-icons/react';
import { SIDEBAR_THEMES } from '@/lib/sidebarThemes';

/**
 * Reusable theme picker for the sidebar background.
 * - `value`: current stored theme id ('' / 'slate' / 'midnight' / etc.)
 * - `onChange(id)`: invoked with the new theme id
 * - `triggerSize`: 'sm' for inline (sidebar profile area) | 'md' for action menus
 * - `align`: passed to Popover
 */
export default function SidebarThemePicker({ value, onChange, triggerSize = 'sm', align = 'end', testIdPrefix = 'theme' }) {
  const [open, setOpen] = useState(false);

  const handleSelect = (id) => {
    setOpen(false);
    onChange?.(id);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {triggerSize === 'sm' ? (
          <button
            className="inline-flex items-center justify-center w-7 h-7 rounded-md hover:bg-white/10 text-current opacity-70 hover:opacity-100 transition-opacity"
            title="Sidebar theme"
            aria-label="Pick sidebar theme"
            data-testid={`${testIdPrefix}-trigger`}
          >
            <Palette size={14} weight="duotone" />
          </button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="rounded-lg border-[#E4E4E7] text-[#52525B] hover:bg-[#F4F4F5]"
            data-testid={`${testIdPrefix}-trigger`}
          >
            <Palette size={13} className="mr-1.5" weight="duotone" /> Theme
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2 rounded-xl" align={align}>
        <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#A1A1AA] px-1.5 py-1">Sidebar Background</p>
        <div className="grid grid-cols-4 gap-1.5 p-1.5" data-testid={`${testIdPrefix}-grid`}>
          {SIDEBAR_THEMES.map(t => {
            const selected = (value || '') === t.id;
            return (
              <button
                key={t.id || 'default'}
                onClick={() => handleSelect(t.id)}
                className={`relative w-10 h-10 rounded-lg border-2 transition-all hover:scale-105 ${selected ? 'border-[#10B981]' : 'border-[#E4E4E7]'}`}
                style={{ background: t.bg }}
                title={t.name}
                data-testid={`${testIdPrefix}-swatch-${t.id || 'default'}`}
              >
                {!t.dark && (
                  <span className="absolute inset-0 rounded-md flex items-center justify-center text-[8px] font-bold text-[#A1A1AA]">A</span>
                )}
                {selected && (
                  <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-[#10B981] flex items-center justify-center shadow-sm">
                    <Check size={9} weight="bold" className="text-white" />
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <p className="text-[10px] text-[#A1A1AA] italic px-1.5 pt-1">
          {SIDEBAR_THEMES.find(t => t.id === (value || ''))?.name || 'Default'}
        </p>
      </PopoverContent>
    </Popover>
  );
}
